/**
 * Group competitions on Event Registration: total participants per team
 * (including the person who validated with B No. + date of birth).
 * Teammate eligibility is enforced numerically in code (sync with `bulandiTeammateBrInAllowedBandForHeader_` in
 * scripts/bulandiRegistrationBoundWebApp.gs). User-facing errors must not quote B-number ranges — use age wording below.
 *
 * Most events use a fixed team size (min === max). Mind Maze allows solo or duet (teammate optional).
 * Sher-Lock&Key allows 2–3 participants (teammate 1 required, teammate 2 optional).
 */
export const BULANDI_GROUP_EVENT_TOTAL_PARTICIPANTS_BY_ID = {
  "funk-fusion-seniors": 2,
  "mind-maze-seniors": 2,
  "sher-lock-key-seniors": 3,
  "knives-out-seniors": 2,
  "clash-of-clans-family": 4,
};

/** Minimum team size when it differs from max (event id → min total including validated participant). */
export const BULANDI_GROUP_EVENT_MIN_TOTAL_PARTICIPANTS_BY_ID = {
  "mind-maze-seniors": 1,
  "sher-lock-key-seniors": 2,
};

export function bulandiIsGroupEventForRegistration(eventId) {
  return Object.prototype.hasOwnProperty.call(
    BULANDI_GROUP_EVENT_TOTAL_PARTICIPANTS_BY_ID,
    eventId,
  );
}

export function bulandiGroupEventMaxTotal(eventId) {
  return BULANDI_GROUP_EVENT_TOTAL_PARTICIPANTS_BY_ID[eventId];
}

export function bulandiGroupEventMinTotal(eventId) {
  if (
    Object.prototype.hasOwnProperty.call(
      BULANDI_GROUP_EVENT_MIN_TOTAL_PARTICIPANTS_BY_ID,
      eventId,
    )
  ) {
    return BULANDI_GROUP_EVENT_MIN_TOTAL_PARTICIPANTS_BY_ID[eventId];
  }
  return bulandiGroupEventMaxTotal(eventId);
}

/** Max teammate B-number fields (excluding the validated participant). */
export function bulandiGroupEventTeammateSlots(eventId) {
  const max = bulandiGroupEventMaxTotal(eventId);
  return typeof max === "number" && max > 1 ? max - 1 : 0;
}

/** Minimum teammate fields that must be filled. */
export function bulandiGroupEventMinTeammateSlots(eventId) {
  const min = bulandiGroupEventMinTotal(eventId);
  return typeof min === "number" && min > 1 ? min - 1 : 0;
}

/** @param {unknown} entry */
function teammateBrFromEntry(entry) {
  if (entry && typeof entry === "object" && "br" in entry) {
    return String(entry.br ?? "").trim();
  }
  return String(entry ?? "").trim();
}

/** @param {unknown} entry */
function teammateDobFromEntry(entry) {
  if (entry && typeof entry === "object" && "dob" in entry) {
    return String(entry.dob ?? "").trim();
  }
  return "";
}

/**
 * Parse teammate form slots into filled entries; enforce required vs optional slots.
 * @param {Array<unknown>} trimmed
 * @param {string} eventId
 * @param {string} eventName
 * @returns {{ entries?: Array<{ br: string; dob: string }>; error?: string }}
 */
export function collectGroupEventTeammateEntries(trimmed, eventId, eventName) {
  const maxSlots = bulandiGroupEventTeammateSlots(eventId);
  const minSlots = bulandiGroupEventMinTeammateSlots(eventId);
  const entries = [];

  for (let i = 0; i < maxSlots; i += 1) {
    const entry = trimmed[i] ?? { br: "", dob: "" };
    const br = teammateBrFromEntry(entry);
    const dob = teammateDobFromEntry(entry);
    const any = Boolean(br || dob);

    if (i < minSlots) {
      if (!br || !dob) {
        const rangeHint =
          maxSlots > minSlots
            ? ` (${minSlots} required, up to ${maxSlots} teammates)`
            : ` (${minSlots} teammate${minSlots > 1 ? "s" : ""})`;
        return {
          error: `Enter teammate ${i + 1} B No. and date of birth for ${eventName}${rangeHint}.`,
        };
      }
      entries.push({ br, dob });
      continue;
    }

    if (!any) continue;
    if (!br || !dob) {
      return {
        error: `Enter both B No. and date of birth for teammate ${i + 1} (${eventName}), or leave the optional teammate blank.`,
      };
    }
    entries.push({ br, dob });
  }

  return { entries };
}

/** Inclusive numeric B (digits only). Internal rules — do not surface ranges in UI copy. */
export function bulandiTeammateBrNumericInAllowedBand(n, eventId) {
  if (n == null || !Number.isFinite(n)) return false;
  if (n < 1001 || n > 10000) return false;
  if (eventId === "clash-of-clans-family") return n >= 1501 && n <= 10000;
  return n >= 3001 && n <= 10000;
}

/** User-facing copy when a teammate fails numeric eligibility checks (no B ranges). */
export function bulandiTeammateBrBandErrorLabel(eventId) {
  if (eventId === "clash-of-clans-family") {
    return "That teammate is not eligible for Clash of Clans — teammates must be in the Intermediates or Seniors age group (not Juniors).";
  }
  return "That teammate is not eligible for this team event — teammates must meet the Seniors age criteria (not Juniors or Intermediates).";
}
